import { useEffect, useState } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { ThemeUtils } from 'src/core/themes';
import {
  SITE_THEME_MODE_STORAGE_KEY,
  SiteThemeOption,
} from 'src/contexts/SiteTheme/SiteThemeContext';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import { DefaultTheme, ThemeProvider } from 'styled-components';

import { SomethingWentWrong } from './SomethingWentWrong';

/**
 * Derives dark mode preference synchronously from localStorage — the same
 * key SiteThemeContextProvider writes to — with a fallback to the OS
 * preference via matchMedia. No context, no network call required.
 */
const getIsDarkMode = (): boolean => {
  const stored = localStorage.getItem(
    SITE_THEME_MODE_STORAGE_KEY
  ) as SiteThemeOption | null;
  if (stored === SiteThemeOption.Dark) return true;
  if (stored === SiteThemeOption.Light) return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
};

/**
 * Loads the legacy theme once, with no context dependencies.
 * Falls back through reachPro → empty object so ThemeProvider always
 * receives a non-null value.
 */
const loadErrorPageTheme = (): Promise<DefaultTheme> => {
  const isDark = getIsDarkMode();
  const themeName = isDark ? 'reachProDark' : 'reachPro';
  return ThemeUtils.loadLegacyTheme(themeName).catch(() =>
    ThemeUtils.loadLegacyTheme('reachPro')
  );
};

/**
 * RootErrorPage — the fallback rendered by RootErrorBoundary when an
 * unhandled React error escapes the component tree.
 *
 * Design constraints:
 *  - Must never itself crash (no provider dependencies that can throw).
 *  - Must not trigger GetAppContext or any authenticated API calls
 *    (those cause the infinite LogEvents + GetAppContext loop seen in
 *    Kusto when the error fires repeatedly).
 *  - Theme is derived from localStorage + matchMedia synchronously,
 *    removing the cobrandId → useEffect re-run race that caused
 *    ThemeProvider to receive an undefined theme (styled-components
 *    error #14).
 */
export const RootErrorPage = (props: FallbackProps) => {
  const routeError = useRouteError();
  const error = routeError ?? props.error;

  const message = ((error ?? {}) as { message?: string }).message;
  const status = isRouteErrorResponse(error) ? error.status : undefined;
  const statusText = isRouteErrorResponse(error) ? error.statusText : undefined;

  // Single atomic state — loading:true and theme:undefined can never
  // be observed separately, closing the race that caused error #14.
  const [theme, setTheme] = useState<DefaultTheme | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadErrorPageTheme().then((t) => {
      if (!cancelled) setTheme(t);
    });
    return () => {
      cancelled = true;
    };
  }, []); // empty deps — fires exactly once, no re-runs

  if (!theme) {
    return <PosSpinner />;
  }

  const { headerDisplay, messageDisplay } = status
    ? getErrorInfoFromStatusCode(status, statusText)
    : { headerDisplay: undefined, messageDisplay: undefined };

  return (
    <ThemeProvider theme={theme}>
      <SomethingWentWrong
        header={headerDisplay}
        message={messageDisplay}
        messages={[message]}
      />
    </ThemeProvider>
  );
};
